/**
 * Activity Screen - MOBILE ONLY
 * 
 * Shows comments, history, and activity timeline for an opportunity.
 * Partners can discuss and track progress on the opportunity.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, ScrollView, TextInput, Pressable, ActivityIndicator, KeyboardAvoidingView, Platform, Alert } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import { ScreenContainer, ScreenHeader } from '../../../components/v2/mobile';
import { authApi, ActivityLogEntry } from '../../../shared/utils/api';
import { logger } from '../../../shared/utils/logger';
import { Comment, CommentThread } from '../../../shared/types/comments';
import { formatUserContent } from '../../../shared/utils/textHelpers';
import { styles } from './styles/activityStyles';

// Action labels for display
const ACTION_LABELS: Record<string, { label: string; icon: string }> = {
  create: { label: 'Created', icon: 'add-circle-outline' },
  update: { label: 'Updated', icon: 'create-outline' },
  delete: { label: 'Deleted', icon: 'trash-outline' },
  pass: { label: 'Passed on', icon: 'close-circle-outline' },
  comment: { label: 'Commented', icon: 'chatbubble-outline' },
  vote: { label: 'Voted', icon: 'thumbs-up-outline' },
  view: { label: 'Viewed', icon: 'eye-outline' },
};

export default function ActivityScreen() {
  const router = useRouter();
  const { portfolioId, opportunityId, opportunityName } = useLocalSearchParams<{
    portfolioId: string; opportunityId: string; opportunityName?: string;
  }>();

  const oppId = opportunityId ? parseInt(opportunityId, 10) : 0;
  const handleBack = useCallback(() => router.back(), [router]);

  const [activeTab, setActiveTab] = useState<'comments' | 'history'>('comments');
  const [thread, setThread] = useState<CommentThread | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get user name from comment
  const getUserName = (comment: Comment) => comment.created_by_user?.display_name || comment.created_by_user?.email?.split('@')[0] || 'Unknown';
  const getUserInitials = (comment: Comment) => getUserName(comment).charAt(0).toUpperCase();

  // Get user name from activity
  const getActivityUserName = (activity: ActivityLogEntry) => activity.user?.display_name || activity.user?.email?.split('@')[0] || 'System';

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!oppId) return;
    try {
      setError(null);
      const response = await authApi.getCommentThreads('property', oppId);
      if (response.success && response.threads?.length > 0) {
        const mainThread = response.threads[0];
        setThread(mainThread);
        const threadResponse = await authApi.getCommentThread(mainThread.id);
        if (threadResponse.success && threadResponse.comments) {
          setComments(threadResponse.comments);
        }
      } else {
        setThread(null);
        setComments([]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch comments';
      logger.error('Failed to fetch comments', err, 'ActivityScreen');
      setError(msg);
    }
  }, [oppId]);

  // Fetch activity history
  const fetchHistory = useCallback(async () => {
    if (!oppId) return;
    setHistoryLoading(true);
    try {
      const response = await authApi.getOpportunityActivity(oppId);
      if (response.success) {
        setActivities(response.activities);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch activity history';
      logger.error('Failed to fetch activity history', err, 'ActivityScreen');
      setError(msg);
    } finally {
      setHistoryLoading(false);
    }
  }, [oppId]);

  // Initial fetch
  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      await fetchComments();
      setLoading(false);
    };
    fetch();
  }, [fetchComments]);

  // Fetch history when tab switches
  useEffect(() => {
    if (activeTab === 'history' && activities.length === 0) {
      fetchHistory();
    }
  }, [activeTab, activities.length, fetchHistory]);

  // Post comment
  const handlePostComment = useCallback(async () => {
    if (!newComment.trim() || !oppId) return;
    setPosting(true);
    try {
      let threadId = thread?.id;
      if (!threadId) {
        const threadResponse = await authApi.createCommentThread({
          context_type: 'property', context_id: oppId, thread_title: opportunityName || 'Discussion',
        });
        if (threadResponse.success && threadResponse.thread) {
          threadId = threadResponse.thread.id;
          setThread(threadResponse.thread);
        } else throw new Error('Failed to create thread');
      }
      const commentResponse = await authApi.createComment({ thread_id: threadId, content: newComment.trim() });
      if (commentResponse.success) {
        setNewComment('');
        Alert.alert('Success', 'Comment posted');
        await fetchComments();
      } else throw new Error('Failed to post comment');
    } catch (err) {
      logger.error('Failed to post comment', err, 'ActivityScreen');
      Alert.alert('Error', err instanceof Error ? err.message : 'Failed to post');
    } finally {
      setPosting(false);
    }
  }, [newComment, oppId, thread, opportunityName, fetchComments]);

  // Render activity item
  const renderActivityItem = (activity: ActivityLogEntry) => {
    const actionConfig = ACTION_LABELS[activity.action] || { label: activity.action, icon: 'ellipse-outline' };
    const userName = getActivityUserName(activity);
    return (
      <View key={activity.id} style={styles.historyItem}>
        <View style={styles.historyIconContainer}>
          <Ionicons name={actionConfig.icon as any} size={16} color={color.textMuted} />
        </View>
        <View style={styles.historyContent}>
          <Text style={styles.historyAction}>
            <Text style={styles.historyActionBold}>{userName}</Text> {actionConfig.label.toLowerCase()}{' '}
            {activity.entity_name && <Text style={styles.historyActionBold}>{activity.entity_name}</Text>}
          </Text>
          <Text style={styles.historyTime}>{formatDate(activity.created_at)}</Text>
          {activity.new_values && Object.keys(activity.new_values).length > 0 && (
            <View style={styles.historyDetails}>
              {Object.entries(activity.new_values).slice(0, 3).map(([key, value]) => (
                <Text key={key} style={styles.historyDetailText}>{key}: {String(value)}</Text>
              ))}
            </View>
          )}
        </View>
      </View>
    );
  };

  return (
    <ScreenContainer>
      <ScreenHeader title={opportunityName || 'Activity'} onBack={handleBack} />
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'} keyboardVerticalOffset={100}>
        {/* TAB BAR */}
        <View style={styles.tabBar}>
          <Pressable style={[styles.tab, activeTab === 'comments' && styles.tabActive]} onPress={() => setActiveTab('comments')}>
            <Ionicons name="chatbubbles-outline" size={icon.md} color={activeTab === 'comments' ? color.primary : color.textMuted} />
            <Text style={[styles.tabText, activeTab === 'comments' && styles.tabTextActive]}>Comments ({comments.length})</Text>
          </Pressable>
          <Pressable style={[styles.tab, activeTab === 'history' && styles.tabActive]} onPress={() => setActiveTab('history')}>
            <Ionicons name="time-outline" size={icon.md} color={activeTab === 'history' ? color.primary : color.textMuted} />
            <Text style={[styles.tabText, activeTab === 'history' && styles.tabTextActive]}>History ({activities.length})</Text>
          </Pressable>
        </View>

        {/* CONTENT */}
        {loading ? (
          <View style={styles.center}><ActivityIndicator size="large" color={color.primary} /></View>
        ) : error ? (
          <View style={styles.center}>
            <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
            <Text style={styles.errorText}>{error}</Text>
            <Pressable onPress={fetchComments} style={styles.retryButton}><Text style={styles.retryText}>Retry</Text></Pressable>
          </View>
        ) : activeTab === 'comments' ? (
          <>
            <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
              {comments.length === 0 ? (
                <View style={styles.emptyState}>
                  <Ionicons name="chatbubble-outline" size={icon.xxl} color={color.textMuted} />
                  <Text style={styles.emptyTitle}>No comments yet</Text>
                  <Text style={styles.emptySubtitle}>Start a conversation about this opportunity</Text>
                </View>
              ) : (
                comments.map(comment => (
                  <View key={comment.id} style={styles.commentCard}>
                    <View style={styles.commentHeader}>
                      <View style={styles.avatar}><Text style={styles.avatarText}>{getUserInitials(comment)}</Text></View>
                      <View style={styles.commentMeta}>
                        <Text style={styles.commentAuthor}>{getUserName(comment)}</Text>
                        <Text style={styles.commentTime}>{formatDate(comment.created_at)}</Text>
                      </View>
                    </View>
                    <Text style={styles.commentContent}>{formatUserContent(comment.content)}</Text>
                  </View>
                ))
              )}
            </ScrollView>
            <View style={styles.inputBar}>
              <TextInput style={styles.input} value={newComment} onChangeText={setNewComment} placeholder="Add a comment..." placeholderTextColor={color.textMuted} multiline maxLength={1000} />
              <Pressable style={[styles.sendButton, (!newComment.trim() || posting) && styles.sendButtonDisabled]} onPress={handlePostComment} disabled={!newComment.trim() || posting}>
                {posting ? <ActivityIndicator size="small" color={color.surface} /> : <Ionicons name="send" size={icon.md} color={color.surface} />}
              </Pressable>
            </View>
          </>
        ) : (
          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            {historyLoading ? (
              <View style={styles.center}><ActivityIndicator size="large" color={color.primary} /></View>
            ) : activities.length === 0 ? (
              <View style={styles.emptyState}>
                <Ionicons name="time-outline" size={icon.xxl} color={color.textMuted} />
                <Text style={styles.emptyTitle}>No activity yet</Text>
                <Text style={styles.emptySubtitle}>Changes to this opportunity will appear here</Text>
              </View>
            ) : (
              activities.map(renderActivityItem)
            )}
          </ScrollView>
        )}
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}
